import type { FollowMode, FollowSignal, FollowSummary, Side, SignalOutcome } from "../api/types";
import { plural } from "./format";
import { parse } from "./money";
import { formatWindow, parseSeriesSlug } from "./series";
import { formatDollars } from "./tape";

/**
 * Чистая часть экрана сигналов: подписи, отбор карточек и сводка двух гипотез.
 *
 * Сеть, таймеры и React живут в компонентах — здесь только правила, которые
 * можно проверить таблицей. То же разделение, что у lib/participants.ts.
 */

/** Порядок режимов. Действующий — строгий МИНИМУМ потолка и выбранного. */
const MODE_RANK: Record<FollowMode, number> = { off: 0, shadow: 1, alert: 2, auto: 3 };

export const FOLLOW_MODES: readonly FollowMode[] = ["off", "shadow", "alert", "auto"];

/** Человеческое имя режима. */
export function modeLabel(mode: FollowMode): string {
  switch (mode) {
    case "off":
      return "выключено";
    case "shadow":
      return "теневой";
    case "alert":
      return "алерты";
    case "auto":
      return "авто";
  }
}

/** Что режим делает с деньгами — то, ради чего переключатель и читают. */
export function modeHint(mode: FollowMode): string {
  switch (mode) {
    case "off":
      return "сигналы не считаются вовсе";
    case "shadow":
      return "сигналы считаются, ордеров нет ни одного";
    case "alert":
      return "карточка с кнопками; деньги тратите вы";
    case "auto":
      return "трейдер ставит ордер сам";
  }
}

/**
 * Поднять режим ВЫШЕ потолка окружения нельзя.
 *
 * Правило дублируется на клиенте не ради безопасности — её держит трейдер,
 * считая минимум сам, — а ради честности кнопки: выбор, который заведомо ничего
 * не изменит, должен быть виден выключенным, а не срабатывать в пустоту.
 */
export function modeAllowed(mode: FollowMode, ceiling: FollowMode | null): boolean {
  // Потолок неизвестен — это НЕ «потолка нет». Ноты трейдера может не быть по
  // двум причинам сразу: он не отвечает либо подсистемы копирования у него нет
  // вовсе (FOLLOW_ENABLED снят, нет POLYGON_RPC_URL, нет PM_GAMMA_URL). В обоих
  // случаях включённая кнопка «авто» обещает беспилотное копирование, которого
  // не будет: запись в Redis пройдёт, режим отрисуется выставленным, а ордеров
  // не появится ни одного. Единственный режим, который в этом состоянии значит
  // ровно то, что написано, — «выключено».
  if (!ceiling) return mode === "off";
  return MODE_RANK[mode] <= MODE_RANK[ceiling];
}

/** Повышение режима — то действие, которое подтверждают в два шага. */
export function raisesRisk(from: FollowMode | null, to: FollowMode): boolean {
  if (!from) return MODE_RANK[to] > MODE_RANK.shadow;
  return MODE_RANK[to] > MODE_RANK[from];
}

/** Человеческое имя исхода сигнала. */
export function outcomeLabel(outcome: SignalOutcome): string {
  switch (outcome) {
    case "shadow":
      return "теневой";
    case "alerted":
      return "ждёт решения";
    case "placed":
      return "ордер выставлен";
    case "skipped":
      return "пропущен вами";
    case "expired":
      return "сгорел";
    case "suppressed":
      return "не взят";
  }
}

/**
 * Почему сигнал не взят — словами.
 *
 * Машинные имена приходят с сервера и там же считаются в ноты; переводятся они
 * здесь. Неизвестное имя показывается как есть: новая версия трейдера не должна
 * давать пустой прочерк вместо причины.
 */
export function ruleLabel(rule: string | undefined): string {
  switch (rule) {
    case undefined:
    case "":
      return "";
    case "window_ends":
      return "окно кончалось: вход уже ничего не решал";
    case "window_not_open":
      return "окно ещё не открылось: страйка нет";
    case "contrarian":
      // Историческое имя: новых строк с ним не появляется, но уже написанные в
      // журнале обязаны читаться по-русски и сегодня.
      return "второй кит ударил в другую сторону";
    case "conflict":
      return "по этому окну у нас уже решение в другую сторону";
    case "price_zone":
      return "цена кита вне зоны сценария";
    case "top_share":
      return "весь перевес дал один кошелёк: это кит, а не семья";
    case "book":
      return "стакана нашего исхода в тот момент не было";
    case "market_taken":
      return "по этому окну ордер уже стоял";
    case "place_failed":
      return "ордер не ушёл: отказала база либо шина";
    case "min_size":
      return "размер вышел ниже минимума рынка";
    case "daily_cap":
      return "дневной лимит подсистемы исчерпан";
    case "not_smart":
      return "кошелёк не в реестре";
    case "too_small":
      return "ставка мельче порога";
    case "sell":
      return "кит продавал: это выход, а не ставка";
    case "self":
      return "это мы сами";
    case "stale":
      return "сигнал опоздал";
    case "market":
      return "рынок неизвестен либо закрыт";
    case "price":
      return "цена не приводится к шагу рынка";
    default:
      return rule;
  }
}

/**
 * Запас, без которого ордер по сигналу отправлять уже поздно.
 *
 * Зеркалит domain.OrderLead и нужен ровно для одного случая — карточки от
 * отправителя, который `decide_by` ещё не шлёт (трейдер и web_api выкатываются
 * одной сборкой, но перезапускаются по одному). Правило дублируется здесь по
 * тому же доводу, что потолок режима в modeAllowed: кнопка, которая заведомо
 * ответит отказом, должна быть видна мёртвой, а не срабатывать в пустоту.
 */
const ORDER_LEAD_MS = 70_000;

/**
 * Крайний срок РЕШЕНИЯ — то, до чего идёт отсчёт на карточке.
 *
 * Это не срок ордера: биржа гасит GTD на минуту раньше срока, ордер короче
 * запаса риск-гейт отвергает целиком, и сервер отказывает по сигналу за
 * ORDER_LEAD_MS до его сгорания. До правки 2026-08-13 карточка считала до
 * `expires_at`, то есть при FOLLOW_ORDER_TTL=2m держала зелёную кнопку 70
 * секунд из 120 в состоянии «любое нажатие вернёт 409».
 *
 * NaN означает «срока нет»: ордера по сигналу не предполагалось.
 */
export function decideBy(signal: FollowSignal): number {
  const decide = Date.parse(signal.decide_by ?? "");
  if (Number.isFinite(decide)) return decide;
  const expires = Date.parse(signal.expires_at);
  return Number.isFinite(expires) ? expires - ORDER_LEAD_MS : Number.NaN;
}

/** Карточка ЖИВА, пока ждёт решения и решение ещё принимается. */
export function isLive(signal: FollowSignal, now: Date): boolean {
  if (signal.outcome !== "alerted") return false;
  const decide = decideBy(signal);
  return Number.isFinite(decide) && decide > now.getTime();
}

/** Сколько осталось на решение, в миллисекундах. */
export function remainingMs(signal: FollowSignal, now: Date): number {
  const decide = decideBy(signal);
  if (!Number.isFinite(decide)) return 0;
  return Math.max(0, decide - now.getTime());
}

/**
 * Карточки, которых сервер в ленте ждущих решения БОЛЬШЕ НЕ НАЗЫВАЕТ.
 *
 * # Зачем сверка, если есть сокет
 *
 * Сигнал приезжает одним сообщением на каждое своё состояние, и «сгорел» — тоже
 * сообщение. Но Pub/Sub доставку не гарантирует, а стор умеет только ДОБАВЛЯТЬ:
 * потерянное сообщение оставляло карточку с живыми кнопками до конца её
 * собственного отсчёта, и нажатие на неё возвращало «сигнал больше не ждёт
 * решения». Ровно этот отказ и наблюдался 2026-08-13 при живом счётчике.
 *
 * # Почему граница по времени обязательна
 *
 * Ответ на `?pending=true` описывает ленту на момент ЗАПРОСА. Сигнал, родившийся
 * позже, в нём законно отсутствует, и погасить его значило бы стереть карточку
 * через секунду после появления. Поэтому гасятся только те, что старше момента,
 * когда запрос ушёл.
 *
 * Сравниваются часы браузера с `detected_at` сервера — те же двое, что уже
 * считают отсчёт карточки. Запас на их расхождение и на дорогу запроса берёт
 * вызывающий, сдвигая `before` назад.
 */
export function vanished(
  known: readonly FollowSignal[],
  pending: readonly FollowSignal[],
  before: number,
): string[] {
  const named = new Set(pending.map((s) => s.id));
  return known
    .filter((s) => s.outcome === "alerted" && !named.has(s.id))
    .filter((s) => Date.parse(s.detected_at) < before)
    .map((s) => s.id);
}

/** Живые карточки, самые свежие сверху. */
export function liveSignals(signals: FollowSignal[], now: Date): FollowSignal[] {
  return signals
    .filter((s) => isLive(s, now))
    .sort((a, b) => Date.parse(b.detected_at) - Date.parse(a.detected_at));
}

/**
 * Какие живые карточки ещё не звучали.
 *
 * Правило звука целиком: звучит ПОЯВЛЕНИЕ, а не состояние. Одна и та же карточка
 * приезжает сообщением на каждое своё изменение (взята, пропущена, сгорела) и
 * перерисовывается посекундным тиком обратного отсчёта — звони она по
 * присутствию, терминал звенел бы раз в секунду всё время жизни карточки.
 *
 * Отсюда же ответ на «а если сигнал перезаписан»: ключ — идентификатор, и
 * замещение записи по нему звука не даёт.
 */
export function unannounced(
  live: readonly FollowSignal[],
  announced: ReadonlySet<string>,
): string[] {
  return live.filter((s) => !announced.has(s.id)).map((s) => s.id);
}

/** История: всё подряд, самые свежие сверху. */
export function historySignals(signals: FollowSignal[]): FollowSignal[] {
  return [...signals].sort((a, b) => Date.parse(b.detected_at) - Date.parse(a.detected_at));
}

/**
 * Отбор карточек по ДЛИТЕЛЬНОСТИ ОКНА серии: 300 — пятиминутки, 900 —
 * пятнадцатиминутки, 0 — все.
 *
 * Единственный отбор страницы сигналов, который считается в браузере, и это не
 * недосмотр. Карточки копирования лежат в общем хранилище, куда их кладут два
 * источника — исторический запрос и опрос живых карточек, идущий на всей
 * странице, — и второй о фильтре не знает по построению. Отрезать историю на
 * сервере значило бы отфильтровать половину списка.
 *
 * Карточка БЕЗ разобранного slug'а остаётся вне выборки: slug пуст, когда рынка
 * нет в справочнике, и длительность такого окна нам неизвестна. Показать его
 * под ярлыком «5м» означало бы соврать; «все» показывает его по-прежнему.
 */
export function ofWindowLen(signals: FollowSignal[], windowLen: number): FollowSignal[] {
  if (!windowLen) return signals;
  return signals.filter((s) => parseSeriesSlug(s.slug ?? "")?.durationSec === windowLen);
}

/** Короткий адрес кошелька — как в ленте. */
export function shortWallet(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/**
 * Чем подписан кит в карточке: именем с биржи, а адресом — только если имени нет.
 *
 * Решение по карточке принимается за секунды, и «0xb55f…64d4» на этой дистанции
 * не читается вовсе: сокращённый адрес не запоминается, не узнаётся и не
 * отличается от соседнего с одного взгляда. Имя же оператор уже видел — в панели
 * участников, в контрагентах, на самой бирже.
 *
 * Полный адрес при этом никуда не девается: он в подсказке и в ссылке, потому
 * что имя кошелёк выбирает себе сам, оно не уникально и опознавать по нему
 * нельзя.
 */
export function walletLabel(signal: FollowSignal): string {
  const name = signal.wallet_name?.trim();
  return name ? name : shortWallet(signal.wallet);
}

/**
 * Рынок сигнала, разобранный на то, что читают: актив, длительность окна и само
 * окно в местном времени.
 *
 * Карточка приходит по окну, на которое оператор не смотрит, и первое, что он
 * спрашивает, — «это вообще про что»: BTC или ETH, пятиминутка или четверть
 * часа. До правки на этом месте стоял вопрос рынка целиком — «Bitcoin Up or Down
 * - August 13, 2:30PM-2:35PM ET», серым, в одну длинную строку: тикер в нём
 * приходится вычитывать, а длительность выводить вычитанием времён.
 *
 * `null` — рынок не из серии (выборы, макрособытия): у него нет ни актива, ни
 * длительности, и выдумывать их нельзя. Разбор — общий с экраном рынка
 * (lib/series.ts), чтобы окно на карточке и в шапке совпадало до минуты.
 *
 * Берёт СЛАГ, а не сигнал: тем же ярлыком подписана карточка замка, а у неё
 * своя запись, и общая тут ровно одна строка. Две копии разбора означали бы два
 * способа назвать одно и то же окно на двух карточках, стоящих в одном макете.
 */
export function marketBadge(
  slug: string | null | undefined,
  now: Date,
): { asset: string; duration: string; window: string } | null {
  const parts = parseSeriesSlug(slug ?? "");
  if (!parts) return null;
  return { asset: parts.asset, duration: parts.duration, window: formatWindow(parts, now) };
}

/**
 * Что кит сделал — словами, для подсказки под значком направления.
 *
 * Направление показывается стрелкой, а стрелка сама по себе неоднозначна: вверх
 * — это «купил» или «поставил на Up»? Подсказка отвечает, и поэтому она не
 * украшение.
 */
export function sideTitle(side: Side): string {
  return side === "BUY" ? "кит купил" : "кит продал";
}

/**
 * Каким цветом показывать величину со знаком.
 *
 * Знак PnL — главное, что читают в блоке про кита, и до правки 2026-08-13 он был
 * набран тем же серым, что и соседние числа: карточка выглядела ровной строкой,
 * в которой «+$37.8» и «−$37.8» различались одним символом. Цвет здесь тот же,
 * что у сторон сделки на всём экране, — зелёный «в плюс», красный «в минус».
 *
 * Ноль и непрочитанное число — приглушённые: цвет обещает знак, а знака у них
 * нет.
 */
export function signTone(raw: string | null | undefined): "buy" | "sell" | "muted" {
  const m = parse(raw);
  if (m === null || m === 0) return "muted";
  return m > 0 ? "buy" : "sell";
}

/** Цвет темы для тона знака. Одно место, где тон превращается в переменную CSS. */
export function toneColor(tone: "buy" | "sell" | "muted"): string {
  switch (tone) {
    case "buy":
      return "var(--color-buy)";
    case "sell":
      return "var(--color-sell)";
    case "muted":
      return "var(--color-muted)";
  }
}

/**
 * Подпись глубины наблюдения рядом с числами кита.
 *
 * Стоит РЯДОМ с PnL, а не в подсказке: своду по одним суткам оператор верит
 * иначе, чем по семи, а звёздочка означает «сутки покрыты обходом не целиком».
 */
export function depthLabel(signal: FollowSignal): string {
  const days = signal.whale.days;
  if (!days) return "";
  return `${days}д${signal.whale.partial ? "*" : ""}`;
}

/**
 * Деньги со ЗНАКОМ: у результата гипотезы знак — половина смысла.
 *
 * Минус типографский, как в остальных подписях интерфейса.
 */
export function signedDollars(raw: string | null | undefined): string {
  const m = parse(raw);
  if (m === null) return "—";
  const sign = m > 0 ? "+" : m < 0 ? "−" : "";
  return `${sign}${formatDollars(String(Math.abs(m) / 1_000_000))}`;
}

/** Одна строка сводки: чем гипотеза кончилась. */
export interface HypothesisRow {
  key: "maker" | "taker" | "real";
  label: string;
  /** Сколько сигналов вошло в сумму. Разное у гипотез — и это надо видеть. */
  count: number;
  pnl: string;
  /** Прочерк вместо числа: гипотеза не посчитана ни по одному сигналу. */
  empty: boolean;
}

/**
 * Сводка двух гипотез в виде строк.
 *
 * # Почему count у каждой свой
 *
 * Мейкерская считается по всем сведённым (неисполнившийся вход даёт честный
 * ноль), тейкерская — только по тем, у кого была цена стакана. Две суммы,
 * посчитанные по разному числу сигналов, сравнивать нельзя, не сказав об этом
 * вслух, — иначе разница между ними читалась бы как разница между входами.
 */
export function hypotheses(summary: FollowSummary): HypothesisRow[] {
  return [
    {
      key: "maker",
      label: `мейкером по цене кита (исполнилось бы ${summary.maker_filled})`,
      count: summary.settled,
      pnl: signedDollars(summary.shadow_maker_pnl),
      empty: summary.settled === 0,
    },
    {
      key: "taker",
      label: "тейкером по вершине стакана, с комиссией",
      count: summary.taker_priced,
      pnl: signedDollars(summary.shadow_taker_pnl),
      empty: summary.taker_priced === 0,
    },
    {
      key: "real",
      // Счётчик — по тем ордерам, что ВОШЛИ в сумму, а не по всем выставленным:
      // сумма считается только по сведённым и исполнившимся, и «12 ордеров, $0»
      // читалось бы как «двенадцать сделок в ноль» там, где правда — «результат
      // ещё неизвестен». Сколько ордеров ушло всего, видно строкой сводки выше.
      label: "фактически выставленные ордера",
      count: summary.real_filled,
      pnl: signedDollars(summary.real_pnl),
      empty: summary.real_filled === 0,
    },
  ];
}

/**
 * Что говорит сводка — одной фразой.
 *
 * Осторожно и без советов: пока сигналов единицы, разница гипотез — шум, и
 * подсказка, объявляющая победителя на трёх сигналах, хуже её отсутствия.
 */
export function verdict(summary: FollowSummary): string {
  if (summary.settled === 0) {
    return "сведённых сигналов пока нет: окна ещё не погасились";
  }
  if (summary.settled < VERDICT_MIN_SIGNALS) {
    return `сведено ${summary.settled} — мало, чтобы сравнивать входы`;
  }
  const maker = parse(summary.shadow_maker_pnl) ?? 0;
  const taker = parse(summary.shadow_taker_pnl) ?? 0;
  if (summary.taker_priced === 0) {
    return "тейкерская гипотеза не посчитана: не было стакана в момент сигналов";
  }
  if (maker === taker) return "гипотезы сошлись";
  return maker > taker
    ? "мейкерский вход впереди на этих данных"
    : "тейкерский вход впереди на этих данных";
}

/**
 * Ниже этого числа сведённых сигналов сводка не называет победителя.
 *
 * Не статистический критерий, а защита от вывода на трёх наблюдениях: разница
 * двух гипотез на паре сигналов — это разница двух подбрасываний монеты.
 */
export const VERDICT_MIN_SIGNALS = 20;

/**
 * Почему лента сигналов пуста — по РАЗМЕРУ РЕЕСТРА, а не по числу сигналов.
 *
 * # Зачем отдельное объяснение
 *
 * Пустая лента означает два совершенно разных состояния, а выглядит одинаково:
 *
 *   реестр НЕ пуст — китов ждём, просто на отслеживаемых окнах они не били;
 *   реестр ПУСТ    — ждать некого, и ждать бессмысленно.
 *
 * Первое чинится временем, второе — порогами FOLLOW_MIN_* в окружении трейдера.
 * Прежняя формулировка называла первую причину всегда, и оператор, у которого
 * реестр пуст по порогам, ждал бы сигналов ровно столько, сколько хватит
 * терпения: подсистема выглядит исправной, кнопка режима горит, ошибок нет.
 *
 * `wallets` приходит строкой из нот трейдера (GET /status). Пусто — трейдер не
 * ответил, и тогда причина неизвестна: называть её «реестр пуст» было бы
 * догадкой о чужом состоянии.
 */
export function emptyFeedReason(wallets: string | undefined): string {
  const n = wallets === undefined || wallets === "" ? null : Number(wallets);
  if (n === null || Number.isNaN(n)) {
    return "сигналов пока не было; размер реестра китов неизвестен — трейдер не ответил";
  }
  if (n === 0) {
    return (
      "сигналов не будет: реестр китов ПУСТ — ни один кошелёк не проходит пороги " +
      "FOLLOW_MIN_* (средняя ставка, число сделок, суток наблюдений, прибыль). " +
      "Это настройка, а не ожидание: пороги в .env трейдера"
    );
  }
  return (
    `сигналов пока не было: в реестре ${n} ` +
    `${plural(n, "кошелёк", "кошелька", "кошельков")}, но на отслеживаемых окнах ` +
    "они не били по стакану"
  );
}

/**
 * Подпись сценария на экране.
 *
 * Машинное имя как есть: оно же стоит в журнале, в ноте /healthz и в ключе
 * Redis, которым оператор понижает режим. Переводить его на русский значило бы
 * завести второе имя одному правилу — и заставить оператора держать в голове
 * соответствие между тем, что он видит, и тем, что он правит в .env.
 *
 * Пусто для строк старше миграции 00021: у них сценария не было вовсе.
 */
export function scenarioLabel(signal: FollowSignal): string {
  return signal.scenario || "";
}

/**
 * Ставим ли мы ПРОТИВ кита.
 *
 * Считается сравнением токенов, а не именем сценария: имя оператор задаёт сам и
 * вправе назвать фейд как угодно, а токены — факт. У следования они совпадают.
 */
export function against(signal: FollowSignal): boolean {
  return Boolean(signal.plan_asset_id) && signal.plan_asset_id !== signal.asset_id;
}

/**
 * Приводит строку ноты к режиму, если она им является.
 *
 * Неизвестное значение — null, а не «как есть»: потолок приезжает СТРОКОЙ из нот
 * трейдера, и подставить в сравнение мусор значило бы разрешить кнопку, за
 * которой нет ни режима, ни ордеров. null здесь читается как «потолок
 * неизвестен», и modeAllowed оставляет из него только «выключено» — fail-closed,
 * как всё в риск-контуре.
 */
export function asFollowMode(raw: string | undefined | null): FollowMode | null {
  if (!raw) return null;
  return (FOLLOW_MODES as readonly string[]).includes(raw) ? (raw as FollowMode) : null;
}
